import { useState } from 'react';

import {
  IconButton,
  SelectDropdown,
  SlideDown,
  useToasterActions,
} from '@opositandopn/ui';
import {
  IoAddCircleOutline,
  IoOptions,
  IoOptionsOutline,
} from 'react-icons/io5';
import { LuFileJson } from 'react-icons/lu';

import { Page } from '../../components/shared/page/Page';
import { THEORY_SUBJECT_ID } from '../../constants/subjects';
import { useProfile } from '../../context/ProfileContext';
import { useExportMultipleChoiceQuestions } from '../../features/questions/api/useMultipleChoiceQuestions';
import { MultipleChoiceQuestionAdminList } from '../../features/questions/components/multiple-choice-question-admin-list/MultipleChoiceQuestionAdminList';
import { MultipleChoiceQuestionEditor } from '../../features/questions/components/multiple-choice-question-editor/MultipleChoiceQuestionEditor';
import {
  QuestionListFilter,
  QuestionListFilterForm,
  toQuestionListRequest,
} from '../../features/questions/components/question-list-filter/QuestionListFilter';
import { Format } from '../../features/reports/api/useErrorReports';
import { formatFileDate } from '../../utils/date';
import { downloadJson } from '../../utils/download';

import styles from './QuestionListPage.module.scss';

function QuestionListPage() {
  const [filter, setFilter] = useState<QuestionListFilterForm>({
    fromTime: new Date('2020-01-01'),
    toTime: new Date(new Date().setHours(23, 59, 59, 999)),
    sort: '-createdAt',
    subject: {
      id: THEORY_SUBJECT_ID,
      name: '',
    },
  });

  const [questionType, setQuestionType] = useState<Format>('multiple_choice');
  const [showFilters, setShowFilters] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const { profile } = useProfile();
  const { sendMessage } = useToasterActions();
  const { mutate: exportQuestions, isPending: isExporting } =
    useExportMultipleChoiceQuestions();

  const handleExport = () => {
    exportQuestions(toQuestionListRequest(filter, profile.id!), {
      onSuccess: ({ json, count }) => {
        downloadJson(`preguntas-${formatFileDate(new Date())}.json`, json);
        sendMessage('¡Hecho!', `${count} preguntas exportadas`, 'success');
      },
    });
  };

  return (
    <Page className={styles['page']}>
      <div className={styles['header']}>
        <IconButton
          icon={<IoAddCircleOutline />}
          onClick={() => {
            setShowNew(true);
          }}
        />
        <SelectDropdown
          className={styles['dropdown']}
          onSelect={(value: string | number) => {
            setQuestionType(value as Format);
          }}
          options={[
            {
              value: 'multiple_choice',
              label: 'Opción múltiple',
              selected: questionType === 'multiple_choice',
            },
            {
              value: 'likert',
              label: 'Escala de Likert',
              disabled: true,
              selected: questionType === 'likert',
            },
            {
              value: 'answer',
              label: 'Respuesta escrita',
              disabled: true,
              selected: questionType === 'answer',
            },
            {
              value: 'fill_the_gap',
              label: 'Rellenar el espacio',
              disabled: true,
              selected: questionType === 'fill_the_gap',
            },
            {
              value: 'true_false',
              label: 'Verdadero o Falso',
              disabled: true,
              selected: questionType === 'true_false',
            },
          ]}
        />
        <IconButton
          icon={<IoOptionsOutline />}
          flipped={showFilters}
          flipIcon={<IoOptions />}
          onClick={() => {
            setShowFilters(!showFilters);
          }}
        />
      </div>
      <SlideDown className={styles['slide-down']}>
        {showFilters ? (
          <QuestionListFilter
            values={filter}
            footerStart={({ dirty }) =>
              questionType === 'multiple_choice' && (
                // Exports the last searched filter, so it waits for "Buscar"
                // while the form has unapplied changes.
                <IconButton
                  icon={<LuFileJson />}
                  title={
                    dirty
                      ? 'Pulsa Buscar para aplicar los filtros antes de exportar'
                      : 'Exportar preguntas filtradas (JSON)'
                  }
                  aria-label="Exportar preguntas filtradas (JSON)"
                  disabled={dirty}
                  loading={isExporting}
                  onClick={handleExport}
                />
              )
            }
            onSubmit={(values) => {
              setFilter(values);
            }}
            onClear={() => {
              setFilter({
                fromTime: new Date('2020-01-01'),
                toTime: new Date(new Date().setHours(23, 59, 59, 999)),
                sort: '-createdAt',
                subject: {
                  id: THEORY_SUBJECT_ID,
                  name: '',
                },
              });
            }}
          />
        ) : null}
      </SlideDown>
      {questionType === 'multiple_choice' && (
        <MultipleChoiceQuestionAdminList filter={filter} />
      )}
      {questionType === 'multiple_choice' && (
        <MultipleChoiceQuestionEditor
          open={showNew}
          onClose={() => setShowNew(false)}
        />
      )}
    </Page>
  );
}

export { QuestionListPage };
